import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { notificationAPI } from '../../services/api';
import { Notification } from '../../types';
import toast from 'react-hot-toast';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markingAsRead: boolean;
  responding: boolean;
  error: string | null;
  lastFetch: string | null;
  hasMore: boolean;
  currentPage: number;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  markingAsRead: false,
  responding: false,
  error: null,
  lastFetch: null,
  hasMore: true,
  currentPage: 0,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (params: { limit: number; offset: number; loadMore?: boolean }, { rejectWithValue }) => {
      try {
        // console.log('📡 API: Fetching notifications...', params);
        const response = await notificationAPI.getNotifications(params);
        // console.log('✅ Notifications response:', response.data);
        return {
          ...response.data,
          loadMore: params?.loadMore || false,
        };
      } catch (error: any) {
        console.error('❌ Fetch notifications error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch notifications');
      }
    }
  );

  export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (_, { rejectWithValue }) => {
      try {
        // console.log('📡 API: Fetching unread count...');
        const response = await notificationAPI.getUnreadCount();
        // console.log('✅ Unread count response:', response.data);
        return response.data.data.unread_count;
      } catch (error: any) {
        console.error('❌ Fetch unread count error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch unread count');
      }
    }
  );
  


export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      // console.log('📡 API: Marking notification as read:', notificationId);
      await notificationAPI.markAsRead(notificationId);
      return notificationId;
    } catch (error: any) {
      console.error('❌ Mark as read error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to mark notification as read');
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      // console.log('📡 API: Marking all notifications as read...');
      await notificationAPI.markAllAsRead();
      return true;
    } catch (error: any) {
      console.error('❌ Mark all as read error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to mark all notifications as read');
    }
  }
);


export const respondToCollaborationInvite = createAsyncThunk(
  'notifications/respondToCollaborationInvite',
  async (payload: {
    notification_id: string;
    collaboration_id: string;
    collaborator_id: string;
    action: 'accept' | 'reject';
    message?: string;
  }, { rejectWithValue }) => {
    try {
      // console.log('📡 API: Responding to collaboration invite:', payload.action);
      const response = await notificationAPI.respondToCollaborationInvite(payload);
      // console.log('✅ Collaboration response:', response.data);
      return {
        notificationId: payload.notification_id,
        action: payload.action,
        response: response.data,
      };
    } catch (error: any) {
      console.error('❌ Collaboration response error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to respond to collaboration invite');
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId: string, { rejectWithValue }) => {
    try {
      // console.log('📡 API: Deleting notification:', notificationId);
      await notificationAPI.deleteNotification(notificationId);
      return notificationId;
    } catch (error: any) {
      console.error('❌ Delete notification error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to delete notification');
    }
  }
);

export const clearAllNotifications = createAsyncThunk(
  'notifications/clearAllNotifications',
  async (_, { rejectWithValue }) => {
    try {
      // console.log('📡 API: Clearing all notifications...');
      await notificationAPI.clearAllNotifications();
      return true;
    } catch (error: any) {
      console.error('❌ Clear all notifications error:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to clear all notifications');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      const incoming = action.payload;
      const index = state.notifications.findIndex(n => n.id === incoming.id);

      if (index !== -1) {
        const existing = state.notifications[index];
        
        // If it was read and now is unread, increment count
        if (existing.is_read && !incoming.is_read) {
          state.unreadCount += 1;
        }
        
        // Remove from current position and move to top
        state.notifications.splice(index, 1);
        state.notifications.unshift(incoming);
      } else {
        state.notifications.unshift(incoming);
        if (!incoming.is_read) {
          state.unreadCount += 1;
        }
      }
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    updateNotificationInState: (state, action: PayloadAction<Partial<Notification> & { id: string }>) => {
      const index = state.notifications.findIndex(n => n.id === action.payload.id);
      if (index !== -1) {
        state.notifications[index] = { ...state.notifications[index], ...action.payload };
      }
    },
    
    removeNotificationFromState: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    
    resetNotifications: (state) => {
      state.notifications = [];
      state.currentPage = 0;
      state.hasMore = true;
      state.lastFetch = null;
    },
    
    markNotificationAsReadInState: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        const { notifications, total, limit, offset, loadMore } = action.payload.data;
        // console.log(action.payload)
        if (loadMore) {
          // Append new notifications for pagination
          state.notifications = [...state.notifications, ...notifications];
        } else {
          // Replace notifications for fresh fetch
          state.notifications = notifications;
        }
        // console.log(state.notifications)
        state.hasMore = state.notifications.length < total;
        state.currentPage = Math.floor(offset / limit) + 1;
        state.lastFetch = new Date().toISOString();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load notifications');
      })
      
      // Mark as read
      .addCase(markAsRead.pending, (state) => {
        state.markingAsRead = true;
        state.error = null;
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        state.markingAsRead = false;
        const notificationId = action.payload;
        // console.log("payload", action.payload)
        var notification = state.notifications.find(n => n.id === notificationId);
        
        if (notification && !notification.is_read) {
          notification.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.markingAsRead = false;
        state.error = action.payload as string;
        toast.error('Failed to mark notification as read');
      })
      
      // Mark all as read
      .addCase(markAllAsRead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.loading = false;
        state.notifications = state.notifications.map(n => ({ ...n, is_read: true }));
        state.unreadCount = 0;
        toast.success('All notifications marked as read');
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to mark all notifications as read');
      })
      
      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(fetchUnreadCount.rejected, (state, action) => {
        state.error = action.payload as string;
        console.error('Failed to fetch unread count');
      })
      
      // Respond to collaboration invite
      .addCase(respondToCollaborationInvite.pending, (state) => {
        state.responding = true;
        state.error = null;
      })
      .addCase(respondToCollaborationInvite.fulfilled, (state, action) => {
        state.responding = false;
        const { notificationId, action: responseAction } = action.payload;
        
        // Update notification as responded
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.is_read = true;
          notification.responded_at = new Date().toISOString();
          if (!notification.is_read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
        
        toast.success(`Collaboration invitation ${responseAction}ed successfully`);
      })
      .addCase(respondToCollaborationInvite.rejected, (state, action) => {
        state.responding = false;
        state.error = action.payload as string;
        toast.error('Failed to respond to collaboration invite');
      })
      
      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notificationId = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        
        // Decrease unread count if notification was unread
        if (notification && !notification.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        
        state.notifications = state.notifications.filter(n => n.id !== notificationId);
        toast.success('Notification deleted');
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to delete notification');
      })
      
      // Clear all notifications
      .addCase(clearAllNotifications.fulfilled, (state) => {
        state.notifications = [];
        state.unreadCount = 0;
        state.currentPage = 0;
        state.hasMore = true;
        toast.success('All notifications cleared');
      })
      .addCase(clearAllNotifications.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to clear all notifications');
      });
  },
});

export const {
  addNotification,
  clearError,
  updateNotificationInState,
  removeNotificationFromState,
  resetNotifications,
  markNotificationAsReadInState,
} = notificationSlice.actions;

export default notificationSlice.reducer;
